import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "common-concepts-in-creating-extensions"
    }}>{`Common Concepts in Creating Extensions`}</h1>
    <p>{`Learn about common concepts, extension registration, and methods that can be used in any extension.`}</p>
    <h2 {...{
      "id": "extension-registration"
    }}>{`Extension Registration`}</h2>
    <p>{`Interaction between UI Extension and Content Fragments console starts with the initialization process that includes extension's capabilities registration so Content Fragments console knows when to invoke the extension. Registration is done by `}<inlineCode parentName="p">{`register`}</inlineCode>{` method provided by `}<inlineCode parentName="p">{`@adobe/uix-guest`}</inlineCode>{` library. This asynchronous method takes single object that describes extension and returns object representing connection to Content Fragments console.`}</p>
    <p>{`Method `}<inlineCode parentName="p">{`register`}</inlineCode>{` should be invoked after extension initialization page is loaded.`}</p>
    <p>{`Extension registration data must include:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`id`}</inlineCode>{` - string with random extension identifier. This identifier useful for debugging of interaction between Content Fragments console and extension and needed if extension provides custom UI.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`methods`}</inlineCode>{` - objects with extension code exposed to Content Fragments console. All methods are grouped into namespaces that represents extension points provided by Content Fragments console.`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { register } from "@adobe/uix-guest";

// ...

const guestConnection = await register({
    id: "extension-id",
    methods: {
        // ...
    }
});
`}</code></pre>
    <h2 {...{
      "id": "extension-ui"
    }}>{`Extension UI`}</h2>
    <p>{`For use-cases when UI Extension provides any data handling or send data to remote service `}<inlineCode parentName="p">{`register`}</inlineCode>{` is the only method that is expected to be invoked.`}</p>
    <p>{`If UI Extension implements own UI it should be provided as separate page. If this UI requires data from Content Fragments console or need to invoke any logic it should establish connection with `}<inlineCode parentName="p">{`attach`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { attach } from "@adobe/uix-guest";

const guestConnection = await attach({ id: "id-used-during-extension-registration" });
`}</code></pre>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`At the moment `}<a parentName="p" {...{
        "href": "#modal"
      }}>{`Modal`}</a>{` is the only way for extension to provide custom UI.`}</p>
    <h2 {...{
      "id": "connection-object"
    }}>{`Connection Object`}</h2>
    <p>{`Both `}<inlineCode parentName="p">{`register`}</inlineCode>{` and `}<inlineCode parentName="p">{`attach`}</inlineCode>{` function of `}<inlineCode parentName="p">{`@adobe/uix-guest`}</inlineCode>{` returns same connection object that has `}<inlineCode parentName="p">{`host`}</inlineCode>{` property and expose API of Content Fragments console exposed for UI Extensions.`}</p>
    <h3 {...{
      "id": "progress-circle"
    }}>{`Progress Circle`}</h3>
    <p>{`A progress circle shows the presence of background system operation in a visual way. The progress circle also blocks all user interactions with the UI.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "38.4375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/uix/docs/static/309fc08b829311b44ae1ece316e6451f/5530d/progress-circle.webp 320w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/0c8fb/progress-circle.webp 640w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/94b1e/progress-circle.webp 1280w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/0b34d/progress-circle.webp 1920w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/d5269/progress-circle.webp 2560w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/0065e/progress-circle.webp 3568w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/uix/docs/static/309fc08b829311b44ae1ece316e6451f/dd4a7/progress-circle.png 320w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/0f09e/progress-circle.png 640w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/bbbf7/progress-circle.png 1280w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/ac7a9/progress-circle.png 1920w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/c7a69/progress-circle.png 2560w", "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/adfc3/progress-circle.png 3568w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/uix/docs/static/309fc08b829311b44ae1ece316e6451f/bbbf7/progress-circle.png",
            "alt": "Progress Circle",
            "title": "Progress Circle",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`The API consist of two methods `}<inlineCode parentName="p">{`start`}</inlineCode>{` and `}<inlineCode parentName="p">{`stop`}</inlineCode>{` which allow to start the progress circle or stop it respectively. An example below introduces a button that starts the progress circle and stops it in 5 seconds.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const guestConnection = await register({
  id: "aem-headless-ui-ext-examples-progress-circle",
  methods: {
    headerMenu: {
      getButtons() {
        return [
          {
            id: "my.company.progress-circle-action",
            label: "Start circle",
            icon: 'OpenIn',
            onClick: () => {
              guestConnection.host.progressCircle.start();
              setTimeout(() => guestConnection.host.progressCircle.stop(), 5000);
            },
          },
        ];
      },
    },
  },
});
`}</code></pre>
    <p>{`Please keep in mind, multiple extensions may use the progress circle simultaneously. The progress circle will not disappear until all involved extensions call `}<inlineCode parentName="p">{`stop`}</inlineCode>{` method.`}</p>
    <p><strong parentName="p">{`API Reference`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Method`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Arguments`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`start`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Shows progress circle and blocks all user input`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`stop`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Stops progress circle and release user input if all other extensions stopped their progress circles`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "toaster"
    }}>{`Toaster`}</h3>
    <p>{`Content Fragments console provides an API for showing small informational messages (toasts) in the bottom part of the UI. These messages are meant to communicate errors, confirm actions and show other notifications to the user. Toaster API is defined in the `}<inlineCode parentName="p">{`toaster`}</inlineCode>{` namespace.`}</p>
    <p>{`In order to display a message an extension must call `}<inlineCode parentName="p">{`display`}</inlineCode>{` method in `}<inlineCode parentName="p">{`toaster`}</inlineCode>{` namespace.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { register } from "@adobe/uix-guest";

const guestConnection = await register({
    id: "my.company.extension-with-taster",
    {
        //...
    }
}

guestConnection.host.toaster.display({
    variant: "positive",
    message: "Toast displayed successfully!",
})
`}</code></pre>
    <p><strong parentName="p">{`API Reference`}</strong></p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`variant`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`neutral`}</inlineCode>{` `}<br />{` `}<inlineCode parentName="td">{`info`}</inlineCode>{` `}<br />{` `}<inlineCode parentName="td">{`negative`}</inlineCode>{` `}<br />{` `}<inlineCode parentName="td">{`positive`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✔️`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Defines type of the message, based on this parameter message will be shown in a different color and different icon`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`message`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`✔️`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Text of the message`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`timeout`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An optional timeout in milliseconds when message should automatically disappear. If not set, default timeout value will be used.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "shared-context"
    }}>{`Shared Context`}</h3>
    <p>{`In order to empower UI Extensions perform useful actions Content Fragments Editor provides access to data that simplifies user authentication and usage of AEM API. Such data may be accessed through `}<inlineCode parentName="p">{`sharedContext`}</inlineCode>{` property of `}<inlineCode parentName="p">{`host`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { attach } from "@adobe/uix-guest";

const guestConnection = await attach({
    id: "my-id"
}
const context = guestConnection.sharedContext;
const aemHost = context.get("aemHost");
`}</code></pre>
    <p>{`Available shared context data:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`{
    aemHost: string, // hostname of connected AEM environment
    locale: string, // locale of current user
    theme: "light" | "dark", // color schema selected by current user
    auth: {
        imsOrg: string, // current IMS organization
        imsToken: string, // user token
        apiKey: string, // API key to use for requests to Adobe services
        imsOrgName: string, // Human readable organization name
        authScheme: "Bearer" // Auth schema that should be used during communication with Adobe services
    }
}
`}</code></pre>
    <h3 {...{
      "id": "get-content-fragment"
    }}>{`Get Content Fragment`}</h3>
    <p>{`You can access data about the Content Fragment that is currently being edited by using the `}<inlineCode parentName="p">{`host.contentFragment.getContentFragment()`}</inlineCode>{` method in an extension:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { register } from "@adobe/uix-guest";
// ...
const init = async () => {
    const registrationConfig = {
        id: extensionId,
        methods: {
            headerMenu: {
                async getButtons() {
                    return [
                        {
                            id: "get-active-cf",
                            label: "Get Active CF / Canvas",
                            onClick: async () => {
                                // Get Content Fragment
                                const contentFragment = await guestConnection.host.contentFragment.getContentFragment();
                            },
                        },
                    ];
                },
            },
        },
    };
    const guestConnection = await register(registrationConfig);
}
init().catch(console.error)
`}</code></pre>
    <h4 {...{
      "id": "result-object"
    }}>{`Result object`}</h4>
    <p>{`This `}<inlineCode parentName="p">{`contentFragment`}</inlineCode>{` object holds the last received state from AEM instance. It does not contain recent changes from the Editor (no edits from the canvas, sidebar or changes to variations) `}<strong parentName="p">{`until they are successfully saved`}</strong>{` in AEM.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`The API is experimental and might change or disappear at any time. The result object structure is part of a low level API that could be changed in the future.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      